import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Activity, ActivityData } from "@/entities/activities/types";
import { useGetAttachment } from "@/entities/attachments/hooks";
import { downloadBlob } from "@/entities/attachments/helpers";
import {
  Calendar,
  Close,
  Import,
  Export,
  Edit,
  Form,
  GoogleMeeting,
  Location as LocationIcon,
  NoteTaker,
  People,
  Recording,
  Send,
  User,
  Zoom
} from "@dreamhub/designsystem/icons";
import OptionsDropdownMenu, {
  DetailedViewOptions
} from "@/components/controls/OptionsDropdownMenu/OptionsDropdownMenu";
import Text from "@/components/ui/text";
import LogoIconContainer from "@/components/ui/logo-icon-container";
import {
  formatFullDate,
  getRelativeTime,
  toCalendarDate
} from "@/utils/datetime";
import { Link } from "react-router-dom";
import IconContainer from "@/components/ui/icon-container";
import UserOrPersonBadge from "./UserOrPersonBadge";
import { cn } from "@/utils/utils";
import { IconButton } from "@/components/ui/icon-button";
import { isEmpty, isNull, map } from "lodash";
import ActivityDetailsSection from "./ActivityDetailsSection";
import { ActivityAttachments } from "./ActivityAttachments";
import { markdownToHtml } from "@/components/controls/TextEditor/utils/markdownConverter";
import ExpandedViewDetail from "./ExpandedViewDetail";
import { useFeatureFlag } from "@/services/features/hook";
import { FeatureFlags } from "@/services/features/types";
import {
  getTranscriptFailureReason,
  isTranscriptProcessing
} from "@/entities/activities/helpers";
import TranscriptProcessingIndicator from "@/components/layouts/Activities/components/TranscriptProcessingIndicator";
import TranscriptUnavailableNotice from "@/components/layouts/Activities/components/TranscriptUnavailableNotice";
import { generateTranscriptFilename } from "@/entities/activities/helpers";
import ExportTranscriptUnavailableModal from "@/components/controls/ExportTranscriptUnavailableModal";

type DetailItemConfig = {
  id: string;
  icon?: JSX.Element;
  title?: string;
  shouldShow: boolean;
  getContent: () => React.ReactNode;
};

interface Props {
  activity: ActivityData;
  title: string;
  icon: string | JSX.Element;
  activityType: string;
  closePopover: () => void;
  people: string;
  time: string | null;
  openEdit?: (activityType: Activity, activityData: ActivityData) => void;
  openDelete?: (activityId: string) => void;
  isExpandedView: boolean;
  onImportTranscript?: (activity: ActivityData) => void;
  onInviteNotetaker?: (activity: ActivityData) => void;
}

const ActivityDetails: React.FC<Props> = ({
  activity,
  title,
  icon,
  activityType,
  closePopover,
  people,
  time,
  openEdit,
  openDelete,
  isExpandedView,
  onImportTranscript,
  onInviteNotetaker
}) => {
  const { t } = useTranslation();
  const { notes, type } = activity;
  const isNoteActivity = type === Activity.NOTE;
  const isMeetingActivity =
    type === Activity.IN_PERSON_MEETING || type === Activity.ONLINE_MEETING;
  const isSlackActivity = type === Activity.SLACK_MESSAGE;
  const isCallActivity = type === Activity.CALL;

  const showPainPointsAndNextSteps =
    isExpandedView &&
    (type === Activity.IN_PERSON_MEETING || type === Activity.ONLINE_MEETING);

  const { enabled: processingIndicatorEnabled } = useFeatureFlag(
    FeatureFlags.CRM_8124_TRANSCRIPT_PROCESSING
  );
  const showTranscriptSection = isMeetingActivity && isExpandedView;
  const hasTranscript = notes?.hasTranscript === true;
  // A call's transcript arrives with its summary, so it is exported, never imported.
  const showCallTranscriptExport =
    isCallActivity && isExpandedView && hasTranscript;
  // Legacy client-side heuristic, kept for the flag-off path.
  const isTranscriptAnalyzing =
    !hasTranscript && !!notes?.transcriptAttachmentId;
  const isProcessing =
    processingIndicatorEnabled && isTranscriptProcessing(notes);
  const transcriptFailureReason = processingIndicatorEnabled
    ? getTranscriptFailureReason(notes)
    : null;

  const { refetch: getTranscriptAttachment } = useGetAttachment(
    notes?.transcriptAttachmentId || ""
  );

  const [isExportUnavailableOpen, setIsExportUnavailableOpen] = useState(false);

  const onDownloadTranscript = useCallback(async () => {
    if (!notes?.transcriptAttachmentId) {
      setIsExportUnavailableOpen(true);
      return;
    }
    try {
      const { data } = await getTranscriptAttachment();
      if (data) {
        const sourceFilename = data.filename ?? "";
        const lastDotIndex = sourceFilename.lastIndexOf(".");
        const extension =
          lastDotIndex > -1 && lastDotIndex < sourceFilename.length - 1
            ? sourceFilename.slice(lastDotIndex + 1)
            : "txt";
        downloadBlob({
          ...data,
          filename: generateTranscriptFilename(notes?.subject, extension)
        });
      }
    } catch (error) {
      console.error("Error downloading transcript:", error);
    }
  }, [getTranscriptAttachment, notes?.subject, notes?.transcriptAttachmentId]);

  const expandedViewDropdownOptions = (): DetailedViewOptions => {
    const options: DetailedViewOptions = {};
    const exportTranscriptOption = {
      action: () => onDownloadTranscript(),
      customIcon: (
        <IconContainer size="5" color="secondary">
          <Export />
        </IconContainer>
      )
    };

    if (onInviteNotetaker) {
      options.inviteNotetaker = {
        action: () => onInviteNotetaker(activity),
        customIcon: (
          <IconContainer size="5" color="secondary">
            <NoteTaker />
          </IconContainer>
        )
      };
    }

    if (showTranscriptSection) {
      if (isProcessing) {
        // While analyzing: export is blocked, but replacing the transcript stays
        // available and opens the existing import modal with its replace warning.
        options.exportTranscript = {
          action: () => {},
          disabled: true,
          disabledTooltipText: t(
            "activities.transcript.exportDisabledDuringProcessing"
          ),
          customIcon: (
            <IconContainer size="5" color="secondary">
              <Export />
            </IconContainer>
          )
        };
        if (onImportTranscript) {
          options.replaceTranscript = {
            action: () => onImportTranscript(activity),
            customIcon: (
              <IconContainer size="5" color="secondary">
                <Import />
              </IconContainer>
            )
          };
        }
      } else if (hasTranscript) {
        options.exportTranscript = exportTranscriptOption;
        if (onImportTranscript) {
          options.replaceTranscript = {
            action: () => onImportTranscript(activity),
            customIcon: (
              <IconContainer size="5" color="secondary">
                <Import />
              </IconContainer>
            )
          };
        }
      } else if (isTranscriptAnalyzing) {
        options.importTranscript = {
          action: () => {},
          disabled: true,
          disabledTooltipText: t(
            "activities.transcript.section.analyzingTooltip"
          ),
          customIcon: (
            <IconContainer size="5" color="secondary">
              <Import />
            </IconContainer>
          )
        };
      } else {
        if (onImportTranscript) {
          options.importTranscript = {
            action: () => onImportTranscript(activity),
            customIcon: (
              <IconContainer size="5" color="secondary">
                <Import />
              </IconContainer>
            )
          };
        }
      }
    } else if (showCallTranscriptExport) {
      options.exportTranscript = exportTranscriptOption;
    }

    if (openDelete) {
      options.delete = {
        action: () => openDelete(activity.id),
        variant: "destructive"
      };
    }

    return options;
  };

  const attachmentsField: DetailItemConfig = {
    id: "attachments",
    title: t("activities.activityModal.attachments"),
    shouldShow: !isEmpty(notes?.attachments),
    getContent: () => <ActivityAttachments attachments={notes?.attachments} />
  };

  const createEmailField = (
    id: string,
    titleKey: string,
    icon: JSX.Element,
    data: any,
    isArray: boolean = false
  ): DetailItemConfig => ({
    id,
    title: t(titleKey),
    icon,
    shouldShow: type === Activity.EMAIL && (isArray ? !isEmpty(data) : !!data),
    getContent: () =>
      isArray
        ? data?.map((email: any) => email.name || email.email).join(", ") || ""
        : data?.name || data?.email || ""
  });

  const emailDetailItemsConfig: DetailItemConfig[] = [
    createEmailField(
      "fromEmail",
      "activities.activityModal.fromEmail",
      <User />,
      notes?.fromEmail
    ),
    createEmailField(
      "toEmails",
      "activities.activityModal.toEmails",
      <Send />,
      notes?.toEmails,
      true
    ),
    createEmailField(
      "ccEmails",
      "activities.activityModal.ccEmails",
      <Send />,
      notes?.ccEmails,
      true
    ),
    attachmentsField
  ];

  const getRecordingDisplay = () => {
    const url = notes?.recordingUrl ?? "";
    if (type === Activity.CALL)
      return {
        icon: <Recording />,
        label: t("activities.activityModal.recordingLink")
      };
    if (url.includes("zoom.us"))
      return {
        icon: <Zoom />,
        label: t("activities.activityModal.zoomRecording")
      };
    if (url.includes("google.com"))
      return {
        icon: <GoogleMeeting />,
        label: t("activities.activityModal.googleMeetRecording")
      };
    return {
      icon: <Recording />,
      label: t("activities.activityModal.meetingRecordingURL")
    };
  };
  const recordingDisplay = getRecordingDisplay();

  const defaultDetailItemsConfig: DetailItemConfig[] = [
    {
      id: "people",
      icon: <People />,
      title: t("activities.activityModal.participants"),
      shouldShow: !isNoteActivity,
      getContent: () => {
        const prefix =
          type === Activity.NDA_SENT ||
          type === Activity.NDA_SIGNED ||
          type === Activity.QUOTE_SENT
            ? t(
                `activities.activityModal.${
                  type === Activity.NDA_SENT || type === Activity.QUOTE_SENT
                    ? "sentTo"
                    : "receivedFrom"
                }`
              )
            : null;

        return (
          <>
            {prefix && <Text className="mr-1">{prefix}</Text>}
            {people}
          </>
        );
      }
    },
    {
      id: "date",
      title: t("activities.activityModal.time"),
      icon: <Calendar />,
      shouldShow: !!notes,
      getContent: () => (
        <>
          <span>
            {formatFullDate(toCalendarDate(notes!.date), false, true)}
          </span>
          {!isNull(time) && !isNoteActivity && <span>{`, ${time}`}</span>}
          <span className="ml-1">
            {`(${getRelativeTime(toCalendarDate(notes!.date))})`}
          </span>
        </>
      )
    },
    {
      id: "location",
      title: t("activities.activityModal.location"),
      icon: <LocationIcon />,
      shouldShow: !!notes?.location,
      getContent: () => notes!.location
    },
    {
      id: "recording",
      icon: recordingDisplay.icon,
      shouldShow:
        (type === Activity.ONLINE_MEETING || type === Activity.CALL) &&
        !!notes?.recordingUrl,
      getContent: () => recordingDisplay.label
    },
    attachmentsField
  ];

  const detailItemsConfig =
    type === Activity.EMAIL ? emailDetailItemsConfig : defaultDetailItemsConfig;

  const renderDetailItem = (config: DetailItemConfig) => {
    if (!config.shouldShow) return null;

    const isLink = config.id === "recording";
    const isAttachments = config.id === "attachments";

    if (isLink) {
      return (
        <div key={config.id} className="flex items-center gap-1">
          <Link
            to={notes!.recordingUrl!}
            target="_blank"
            rel="noopener noreferrer"
            className="w-fit group flex items-center gap-1"
          >
            {config?.icon && (
              <IconContainer
                color="link"
                className="transition-colors duration-200 ease-in-out group-hover:!text-link-hover"
              >
                {config.icon}
              </IconContainer>
            )}
            <Text className="transition-colors duration-200 ease-in-out text-link group-hover:text-link-hover">
              {config.getContent()}
            </Text>
          </Link>
        </div>
      );
    }

    if (isExpandedView) {
      return (
        <ExpandedViewDetail
          key={config.id}
          title={config.title || ""}
          icon={config?.icon}
          content={
            <div className="flex items-center gap-1">
              {isAttachments ? (
                config.getContent()
              ) : (
                <Text
                  className={cn(
                    config.id !== "date" && "flex",
                    config.id === "location" && "break-all"
                  )}
                >
                  {config.getContent()}
                </Text>
              )}
            </div>
          }
        />
      );
    }

    // Minimized view
    if (isAttachments) {
      return <div key={config.id}>{config.getContent()}</div>;
    }

    return (
      <div key={config.id} className="flex items-center gap-1">
        {config?.icon && (
          <IconContainer size="sm" color="secondary">
            {config.icon}
          </IconContainer>
        )}
        <Text variant="bodySecondary">{config.getContent()}</Text>
      </div>
    );
  };

  const isEmptyAdditionalFields = () => {
    const isSummaryEmpty =
      !notes?.summary ||
      Object.values(notes.summary).every(
        (value) =>
          value === null ||
          value === undefined ||
          (typeof value === "string" && !value.trim()) ||
          (Array.isArray(value) && value.length === 0)
      );
    const isContentEmpty = !notes?.emailContent && !notes?.messageContent;
    return isSummaryEmpty && isContentEmpty;
  };

  const AdditionalDetails = () => {
    if (isExpandedView && isEmptyAdditionalFields()) {
      return (
        <div className="flex w-full justify-center items-center p-6">
          <div className="flex flex-col justify-center items-center">
            <Form />
            <div className="flex flex-col gap-2 items-center">
              <Text variant="lgBody">
                {t("activities.activityModal.emptyDetails.title")}
              </Text>
              <Text variant="bodySecondary">
                {t("activities.activityModal.emptyDetails.subtitle", {
                  type: activityType
                })}
              </Text>
            </div>
          </div>
        </div>
      );
    }
    return (
      <>
        {notes?.summary?.summary && (
          <ActivityDetailsSection
            isExpandedView={isExpandedView}
            title={t("activities.summary")}
            content={
              isNoteActivity ||
              isMeetingActivity ||
              isSlackActivity ||
              isCallActivity ? (
                <span
                  className="[&_h1]:text-xl [&_h1]:font-semibold [&_h1]:capitalize [&_h1]:text-primary [&_h1]:my-4 [&_h2]:text-[18px] [&_h2]:font-semibold [&_h2]:text-primary [&_h2]:my-3 [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:leading-[22px] [&_h3]:text-primary [&_h3]:my-2 [&_p]:text-base [&_p]:font-normal [&_p]:leading-5 [&_p]:text-primary [&_p]:my-2 [&_ul]:text-primary [&_ol]:text-primary [&_li]:text-primary [&_li]:font-normal [&_li]:text-base [&_li]:leading-5 [&_ul>li]:list-disc [&_ol>li]:list-decimal [&_ul]:ml-6 [&_ol]:ml-6 [&_li]:ml-0 [&_a]:text-link [&_a]:underline [&_a]:cursor-pointer hover:[&_a]:text-link-hover"
                  dangerouslySetInnerHTML={{
                    __html: markdownToHtml(notes?.summary?.summary || "")
                  }}
                />
              ) : (
                <Text as="span">{notes?.summary?.summary}</Text>
              )
            }
          />
        )}
        {type === Activity.EMAIL && notes?.emailContent && (
          <ActivityDetailsSection
            isExpandedView={isExpandedView}
            title={t("activities.activityModal.emailContent")}
            hideTitle
            content={
              <Text variant="body">
                <span
                  className="[&_h1]:text-xl [&_h1]:font-semibold [&_h1]:capitalize [&_h1]:text-primary [&_h1]:my-4 [&_h2]:text-[18px] [&_h2]:font-semibold [&_h2]:text-primary [&_h2]:my-3 [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:leading-[22px] [&_h3]:text-primary [&_h3]:my-2 [&_p]:text-base [&_p]:font-normal [&_p]:leading-5 [&_p]:text-primary [&_p]:my-2 [&_ul]:text-primary [&_ol]:text-primary [&_li]:text-primary [&_li]:font-normal [&_li]:text-base [&_ul>li]:list-disc [&_ol>li]:list-decimal [&_ul]:ml-6 [&_ol]:ml-6 [&_li]:ml-0 [&_a]:text-link [&_a]:underline [&_a]:cursor-pointer hover:[&_a]:text-link-hover"
                  dangerouslySetInnerHTML={{
                    __html: markdownToHtml(notes?.emailContent || "")
                  }}
                />
              </Text>
            }
          />
        )}
        {notes?.messageContent && (
          <ActivityDetailsSection
            isExpandedView={isExpandedView}
            title={t("activities.activityModal.messageContent")}
            content={<Text variant="body">{notes?.messageContent}</Text>}
          />
        )}
        {showPainPointsAndNextSteps && (
          <ActivityDetailsSection
            isExpandedView={isExpandedView}
            expandedClassName="border-t border-border-border1"
            title={t("activities.activityModal.painPoints")}
            content={
              notes?.summary?.painPoints ? (
                <ul className="list-disc ml-4">
                  {map(notes?.summary?.painPoints, (point, index) => (
                    <li key={index}>
                      <div className="flex flex-col">
                        <Text>{point.description}</Text>
                        <div className="flex gap-1 items-center">
                          <Text variant="bodySecondary">
                            {t("activities.activityModal.raisedBy")}
                          </Text>
                          <UserOrPersonBadge id={point?.raisedBy} />
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <Text
                  variant="bodySecondary"
                  className="flex w-full justify-center"
                >
                  {t("activities.emptyPainPoints")}
                </Text>
              )
            }
          />
        )}
        {showPainPointsAndNextSteps && (
          <ActivityDetailsSection
            isExpandedView={isExpandedView}
            expandedClassName="border-t border-border-border1"
            title={t("activities.activityModal.actionItems")}
            content={
              notes?.summary?.actionItems ? (
                <ul className="list-disc ml-4">
                  {(
                    notes?.summary?.actionItems as {
                      item: string;
                      assignedTo: string;
                    }[]
                  )?.map((actionItem, index) => (
                    <li key={index}>
                      <div className="flex flex-col">
                        <Text>{actionItem.item}</Text>
                        <div className="flex gap-1 items-center">
                          <Text variant="bodySecondary">
                            {t("activities.activityModal.assignedTo")}
                          </Text>
                          <UserOrPersonBadge id={actionItem?.assignedTo} />
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <Text
                  variant="bodySecondary"
                  className="flex w-full justify-center"
                >
                  {t("activities.emptyNextSteps")}
                </Text>
              )
            }
          />
        )}
        {isExpandedView &&
          isSlackActivity &&
          !isEmpty(notes?.summary?.keyTopics) && (
            <ActivityDetailsSection
              isExpandedView={isExpandedView}
              expandedClassName="border-t border-border-border1"
              title={t("activities.activityModal.keyTopics")}
              content={
                <ul className="list-disc ml-4">
                  {map(notes?.summary?.keyTopics, (topic, index) => (
                    <li key={index}>
                      <Text>{topic}</Text>
                    </li>
                  ))}
                </ul>
              }
            />
          )}
        {isExpandedView &&
          isSlackActivity &&
          !isEmpty(notes?.summary?.actionItems) && (
            <ActivityDetailsSection
              isExpandedView={isExpandedView}
              expandedClassName="border-t border-border-border1"
              title={t("activities.activityModal.actionItems")}
              content={
                <ul className="list-disc ml-4">
                  {(notes?.summary?.actionItems as string[])?.map(
                    (actionItem, index) => (
                      <li key={index}>
                        <Text>{actionItem}</Text>
                      </li>
                    )
                  )}
                </ul>
              }
            />
          )}
      </>
    );
  };

  return (
    <div
      className="flex flex-col w-full h-full"
      data-test={`activity-details-${activity.id}`}
    >
      {/* Hidden element to capture initial focus */}
      <div tabIndex={0} className="sr-only" />
      <div className="flex items-center justify-between gap-4 p-4 border-b-[0.5px] border-border-border1">
        <div className="flex items-center gap-4 flex-1 min-w-0">
          <LogoIconContainer
            index={-1}
            bgColor="var(--grey-800)"
            className="size-8 rounded-[8px]"
            innerClassName="rounded-[8px]"
            iconSize="sm"
            icon={icon}
          />
          <div className="flex items-center gap-2 min-w-0">
            <Text
              variant="lgLabel"
              className="text-xl font-semibold truncate min-w-0"
            >
              {isNoteActivity ? t("activities.note") : title}
            </Text>
            {!isNoteActivity && (
              <Text variant="bodySecondary" className="shrink-0">
                {`(${activityType})`}
              </Text>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!isSlackActivity && openEdit && (
            <IconButton
              size="md"
              iconColor="primary"
              variant="icon"
              onClick={() => openEdit(activity.type as Activity, activity)}
              tooltipText={t("activities.activityModal.editActivity")}
              tabIndex={-1}
              autoFocus={false}
            >
              <Edit />
            </IconButton>
          )}
          {isExpandedView && (
            <OptionsDropdownMenu
              options={expandedViewDropdownOptions()}
              tPrefix="activities.options"
              buttonVariant="icon"
              iconSize="md"
              iconColor="primary"
            />
          )}
          <IconButton
            size="md"
            iconColor="primary"
            variant="iconNoBg"
            onClick={closePopover}
            tabIndex={-1}
          >
            <Close />
          </IconButton>
        </div>
      </div>
      <div
        className={cn(
          "flex",
          isExpandedView
            ? "min-h-[70vh] max-h-[70vh]"
            : "overflow-y-auto p-4 gap-6 flex-col max-h-[20rem]"
        )}
      >
        <div
          className={cn(
            isExpandedView &&
              "w-[360px] flex-shrink-0 overflow-hidden border-r-[0.5px] border-border-border1"
          )}
        >
          <ActivityDetailsSection
            isExpandedView={isExpandedView}
            title={t("activities.details")}
            expandedClassName="gap-4"
            hideDivider
            hideTitle={!isExpandedView}
            content={<>{detailItemsConfig.map(renderDetailItem)}</>}
          />
        </div>
        {isExpandedView ? (
          <div
            className={cn(
              "flex flex-col w-full overflow-y-auto",
              isEmptyAdditionalFields() &&
                !((isProcessing || !!transcriptFailureReason) &&
                  isMeetingActivity) &&
                "justify-center items-center"
            )}
          >
            {isProcessing && isMeetingActivity && (
              <TranscriptProcessingIndicator className="mb-4" />
            )}
            {!isProcessing && !!transcriptFailureReason && isMeetingActivity && (
              <TranscriptUnavailableNotice
                reason={transcriptFailureReason}
                className="mb-4"
              />
            )}
            <AdditionalDetails />
          </div>
        ) : (
          <AdditionalDetails />
        )}
      </div>
      <ExportTranscriptUnavailableModal
        labels={{
          title: t("activities.transcript.exportUnavailableTitle"),
          body: t("activities.transcript.exportUnavailableBody"),
          confirm: t("activities.transcript.gotIt")
        }}
        isOpen={isExportUnavailableOpen}
        onClose={() => setIsExportUnavailableOpen(false)}
      />
    </div>
  );
};

export default ActivityDetails;
